import { TagRounding } from '@leland-dev/leland-ui-library';
import React, { type FC, useMemo, useState } from 'react';

import { OrganizationType } from '../common/OrganizationImg';
import OrganizationTag from '../common/OrganizationTag';

import {
  type ReviewOutcomeJobOfferInfoFragment,
  type ReviewOutcomeOutcomeFragment,
  type ReviewOutcomeSchoolAdmissionInfoFragment,
  type ReviewOutcomeTestScoreV2Fragment,
} from './__generated-gql-types__/ReviewOutcome.generated';

interface ReviewOutcomeProps {
  outcomes: ReviewOutcomeOutcomeFragment[];
  clientName: string;
  className?: string;
}

export const ReviewOutcome: FC<ReviewOutcomeProps> = ({
  outcomes,
  clientName,
  className = '',
}) => {
  const {
    schoolOutcomes,
    careerOutcomes,
    testOutcomes,
  }: {
    schoolOutcomes?: ReviewOutcomeSchoolAdmissionInfoFragment[];
    careerOutcomes?: ReviewOutcomeJobOfferInfoFragment[];
    testOutcomes?: Possible<ReviewOutcomeTestScoreV2Fragment>;
  } = useMemo(() => {
    if (!outcomes) {
      return {};
    }
    const schoolOutcomes = outcomes.filter(
      (o): o is ReviewOutcomeSchoolAdmissionInfoFragment =>
        o.__typename === 'SchoolAdmissionInfo',
    );
    const careerOutcomes = outcomes.filter(
      (o): o is ReviewOutcomeJobOfferInfoFragment =>
        o.__typename === 'JobOfferInfo',
    );
    const testOutcomes = outcomes.find(
      (o): o is ReviewOutcomeTestScoreV2Fragment =>
        o.__typename === 'TestScoreV2',
    );
    return { schoolOutcomes, careerOutcomes, testOutcomes };
  }, [outcomes]);

  const [expandedOutcomes, setExpandedOutcomes] = useState(false);

  const outcomeSectionHeading = outcomes?.length
    ? `${clientName} ${
        careerOutcomes?.length
          ? 'landed a role at:'
          : schoolOutcomes?.length
            ? 'was accepted to:'
            : `scored ${testOutcomes?.score} on the ${testOutcomes?.testName}`
      }`
    : null;

  const outcomesToUse: Possible<
    Array<
      | ReviewOutcomeJobOfferInfoFragment
      | ReviewOutcomeSchoolAdmissionInfoFragment
    >
  > = careerOutcomes?.length ? careerOutcomes : schoolOutcomes;

  const outcomesVisible = expandedOutcomes
    ? outcomesToUse
    : outcomesToUse?.slice(0, 3);

  const isCareerOutcome = (
    value:
      | ReviewOutcomeJobOfferInfoFragment
      | ReviewOutcomeSchoolAdmissionInfoFragment,
  ): value is ReviewOutcomeJobOfferInfoFragment => {
    return 'company' in value;
  };

  return outcomes?.length ? (
    <div className={className}>
      <p className="font-medium text-leland-gray-light">
        {outcomeSectionHeading}
      </p>
      {outcomesVisible?.length ? (
        <div className={`flex flex-wrap items-center leading-6`}>
          {outcomesVisible.map((outcome) => {
            return isCareerOutcome(outcome) ? (
              <OrganizationTag
                key={outcome.company.id}
                className="mr-2 mt-2 w-fit"
                tagDetails={{
                  organization: {
                    name: outcome.company.name,
                    logoLink: outcome.company.logoLink,
                  },
                  largeIcons: true,
                  rounding: TagRounding.SM,
                }}
                squareIcon
                type={OrganizationType.COMPANY}
              />
            ) : (
              <OrganizationTag
                key={outcome.school.id}
                className="mr-2 mt-2 w-fit"
                tagDetails={{
                  organization: {
                    name: outcome.school.name,
                    logoLink: outcome.school.logoLink,
                  },
                  largeIcons: true,
                  rounding: TagRounding.SM,
                }}
                squareIcon
                type={OrganizationType.SCHOOL}
              />
            );
          })}
        </div>
      ) : null}
      {outcomesToUse && outcomesToUse.length > 3 ? (
        <button
          className="link link--primary mt-0.5 text-base font-medium"
          onClick={() => setExpandedOutcomes((prevState) => !prevState)}
          id="review_outcome--toggle_outcomes"
        >
          {expandedOutcomes ? 'Hide' : 'Show more'}
        </button>
      ) : null}
    </div>
  ) : null;
};
